/**
 * Commande client — lots 5–6 : retrait, paiement en ligne, empreinte, no-show.
 */

import type { PriceUnit } from './catalog';
import { formatEurFromCents } from './tiers';

export type OrderStatus =
  | 'pending_payment'
  | 'paid_online'
  | 'reserved_onsite'
  | 'picked_up'
  | 'no_show'
  | 'cancelled';

export type PaymentMethod = 'online' | 'onsite_cash' | 'onsite_card';

export const PAYMENT_DISCLOSURE = {
  penaltyLabel: '5 €',
  freeCancellationHours: 24,
  holdExplanation:
    'Une empreinte bancaire garantit votre réservation. Elle n’est pas débitée sauf non-retrait signalé par le producteur (forfait 5 €).',
  onlineExplanation: 'Paiement immédiat sécurisé sur Champ’Table.',
} as const;

export interface OrderItem {
  readonly id: string;
  readonly productId: string;
  readonly productName: string;
  readonly quantity: number;
  readonly priceType: string;
  readonly unitPriceCents: number;
  readonly lineTotalCents: number;
  readonly extrasLabel?: string | null;
}

export interface Order {
  readonly id: string;
  readonly orderNumber: string;
  readonly producerProfileId: string;
  readonly pickupPointId: string;
  readonly pickupSlotId: string;
  readonly pickupStartsAt: string;
  readonly pickupEndsAt: string;
  readonly status: OrderStatus;
  readonly paymentMethod: PaymentMethod;
  readonly totalCents: number;
  readonly confirmToken: string;
  readonly pickedUpAt: string | null;
  readonly cancellationReason: string | null;
  readonly createdAt: string;
  readonly items: readonly OrderItem[];
}

export function quantityStep(priceType: string, custom?: number): number {
  if (custom !== undefined && Number.isFinite(custom) && custom > 0) {
    return custom;
  }
  return priceType === 'kg' || priceType === 'litre' ? 0.1 : 1;
}

export function formatOrderTotal(order: Pick<Order, 'totalCents'>): string {
  return formatEurFromCents(order.totalCents);
}

export function formatLineQuantity(quantity: number, priceType: string, unit: PriceUnit): string {
  const formatted = Number.isInteger(quantity) ? String(quantity) : quantity.toFixed(1).replace('.0', '');
  return `${formatted} ${unit}`;
}

export function orderStatusLabel(status: OrderStatus): string {
  switch (status) {
    case 'pending_payment':
      return 'En attente de paiement';
    case 'paid_online':
      return 'Payée en ligne';
    case 'reserved_onsite':
      return 'Réservée';
    case 'picked_up':
      return 'Retirée';
    case 'no_show':
      return 'Non retirée';
    case 'cancelled':
      return 'Annulée';
  }
}

export function paymentMethodLabel(method: PaymentMethod): string {
  switch (method) {
    case 'online':
      return 'Payé en ligne';
    case 'onsite_card':
      return 'Carte sur place';
    case 'onsite_cash':
      return 'Espèces sur place';
  }
}
